import type { Color, VisualConfig, VisualizationType } from '../../types';
import { VisualizationType as VizType } from '../../types';

/**
 * Color palette for distinct visual identification of different people.
 * Each color is designed to be visually distinct from others.
 */
const COLOR_PALETTE: Color[] = [
  { r: 255, g: 100, b: 100, a: 1 }, // Red
  { r: 100, g: 255, b: 100, a: 1 }, // Green
  { r: 100, g: 100, b: 255, a: 1 }, // Blue
  { r: 255, g: 255, b: 100, a: 1 }, // Yellow
  { r: 255, g: 100, b: 255, a: 1 }, // Magenta
  { r: 100, g: 255, b: 255, a: 1 }, // Cyan
  { r: 255, g: 165, b: 100, a: 1 }, // Orange
  { r: 200, g: 100, b: 255, a: 1 }, // Purple
];

/**
 * Visualization types eligible for random assignment to a newly detected person.
 *
 * Only PER_PERSON visualizations are included. SCENE-scoped visualizations (e.g.
 * DEPARTURE_BOARD) are canvas-wide and take over the whole frame, so they are
 * opt-in via the visualization selector / gesture cycling rather than being
 * randomly assigned to an individual.
 */
const VISUALIZATION_TYPES: VisualizationType[] = [
  VizType.PARTICLE_TRAILS,
  VizType.GEOMETRIC_SHAPES,
  VizType.FLOWING_RIBBONS,
];

/**
 * VisualizationAssigner assigns random visualizations and colors to new people.
 * 
 * Responsibilities:
 * - Randomly select visualization type from available options
 * - Randomly assign colors from distinct color palette
 * - Create complete VisualConfig for new people
 * 
 * Design:
 * - Uses predefined color palette for visual distinction
 * - Random selection ensures variety
 * - No persistence - re-entering people get new assignments
 */
export class VisualizationAssigner {
  /**
   * Assign a random visualization type from available options.
   * 
   * @returns Randomly selected VisualizationType
   */
  assignVisualizationType(): VisualizationType {
    const randomIndex = Math.floor(Math.random() * VISUALIZATION_TYPES.length);
    const selected = VISUALIZATION_TYPES[randomIndex];
    if (!selected) {
      return VizType.PARTICLE_TRAILS; // Fallback to default
    }
    return selected;
  }

  /**
   * Assign a random color from the color palette.
   * 
   * @returns Randomly selected Color
   */
  assignRandomColor(): Color {
    const randomIndex = Math.floor(Math.random() * COLOR_PALETTE.length);
    const selected = COLOR_PALETTE[randomIndex];
    if (!selected) {
      return { r: 255, g: 100, b: 100, a: 1 }; // Fallback to red
    }
    return { ...selected };
  }

  /**
   * Create a complete VisualConfig with random colors and default settings.
   * 
   * @returns New VisualConfig with random primary and secondary colors
   */
  createVisualConfig(): VisualConfig {
    return {
      primaryColor: this.assignRandomColor(),
      secondaryColor: this.assignRandomColor(),
      trailLength: 30, // Default trail length
      trailOpacity: 0.8, // Default opacity
      stickFigureThickness: 2, // Default thickness
    };
  }

  /**
   * Get the color palette for testing or display purposes.
   * 
   * @returns Copy of the color palette array
   */
  getColorPalette(): Color[] {
    return COLOR_PALETTE.map(color => ({ ...color }));
  }

  /**
   * Get the available visualization types for testing or display purposes.
   * 
   * @returns Copy of the visualization types array
   */
  getVisualizationTypes(): VisualizationType[] {
    return [...VISUALIZATION_TYPES];
  }
}
